<template>
  <!-- Loading -->
  <mc-loading-indicator
    v-if="airLoading && !firstLoad"
    class="card card--loading"
    variant="spinner"
    appearance="primary"
    fit="large"
    :label="$t('common.loading')"
  />

  <div v-else class="air-lcl-bookings">
    <!-- Filters -->
    <div data-test="air-lcl-shipments-filters" class="table-filters">
      <TableFilters
        :loading="airLoading"
        @update-component-time-frame="updateComponentTimeFrame"
        @reference-search="referenceSearch"
        @reset-reference-search="getAirBookings($route?.path?.toLowerCase()?.includes?.('export'))"
      />
      <JsonToCSV :filterValue="timeFrame" view-name="Air" :bookings="airData" :pageNumber="airPaginationToken" />
    </div>

    <!-- No results -->
    <MarketingNotification
      v-if="airData && airData.length === 0 && !airError && !airLoading"
      class="card"
      data-test="no-results-air"
    >
      <p
        tabindex="0"
        role="button"
        @keydown.enter="marketingClickThrough"
        @click="marketingClickThrough"
        v-html="$t('shipments.airNotification')"
      />

      <mc-button :label="$t('shipments.airButton')" class="mt-2 mb-4" @click="navigateToAirBooking" />
    </MarketingNotification>

    <!-- Error -->
    <mc-notification
      v-else-if="airData && airData.length === 0 && airError && !airLoading"
      class="card"
      width="fit-content"
      appearance="error"
      icon="exclamation-circle"
      data-test="error-air"
      :body="$t('datatable.error.noRecords500')"
    />

    <!-- Results -->
    <mc-table
      v-else
      class="air-lcl__table"
      fit="small"
      sortdisabled
      :data.prop="airData"
      :columns.prop="[
        {
          id: 'airwayBill',
          label: $t('common.airwayBill'),
          subDataLabel: $t('tables.references'),
          verticalAlign: 'top'
        },
        {
          id: 'from',
          label: $t('common.from'),
          subDataLabel: $t('tables.depAirport'),
          verticalAlign: 'top'
        },
        {
          id: 'to',
          label: $t('common.to'),
          subDataLabel: $t('tables.arrAirport'),
          verticalAlign: 'top'
        },
        {
          id: 'cargo',
          label: $t('common.cargo'),
          verticalAlign: 'top'
        }
      ]"
      data-test="air-table"
    >
      <template v-for="item in airData" :key="item.id">
        <div :slot="`${item.id}_airwayBill`">
          <a
            v-if="item.tpDocNum"
            :href="`/tracking/air/${item.tpDocNum}`"
            class="color-primary"
            @click="PTClickThrough"
          >
            <b>{{ item.tpDocNum }}</b></a
          >
          <br />
          <CustomerReferences v-if="item.references" :references="item.references" />
        </div>
        <div :slot="`${item.id}_from`">
          <FromTo :place="item?.placeOfReceipt || item?.portOfLoading" :port="item?.portOfLoading" />
        </div>
        <div :slot="`${item.id}_to`">
          <FromTo
            :place="item?.placeOfDelivery || item?.portOfDischarge"
            :port="item?.portOfDischarge"
            type="arrival"
          />
        </div>
        <div :slot="`${item.id}_cargo`">
          <Cargo
            :commodity="item?.cargo?.commodity"
            :packageQuantity="item?.cargo?.packageQuantity"
            :packageQuantityUnit="item?.cargo?.packageQuantityUnit"
          />
        </div>
      </template>
    </mc-table>

    <Pagination
      v-if="airData && airData.length > 0 && airLastPage > 1"
      activeView="air"
      :lastPage="airLastPage"
      :totalCount="airTotalCount"
      :currentPage="airPaginationToken"
      @update-data-source="getData"
    />
  </div>
</template>
<script>
import MarketingNotification from "@/components/shared/MarketingNotification.vue";
import Pagination from "@/components/shared/Pagination.vue";
import { mapActions, mapState, mapWritableState } from "pinia";
import { filters } from "@/store/filters";
import { airLcl } from "@/store/airLcl";
import { dateFormat } from "@/lib/filters";
import { shipments } from "@/store/shipments";
import TableFilters from "@/components/filters/TableFilters.vue";
import CustomerReferences from "@/components/shared/CustomerReferences.vue";
import JsonToCSV from "@/components/shared/JsonToCSV.vue";
import FromTo from "@/components/shared/FromTo.vue";
import Cargo from "@/components/shared/Cargo.vue";
import "@maersk-global/mds-components-core/mc-table";

export default {
  name: "Air",
  components: { Pagination, MarketingNotification, TableFilters, CustomerReferences, JsonToCSV, FromTo, Cargo },
  data() {
    return {
      selectedRow: null,
      firstLoad: false
    };
  },
  computed: {
    ...mapState(airLcl, ["airData", "airError", "airLastPage", "airTotalCount", "airLoading", "airPaginationToken"]),
    ...mapState(filters, ["activeFilter"]),
    ...mapState(shipments, ["timeFrame", "refSearch"]),
    ...mapWritableState(shipments, ["timeFrame"]),
    isExport() {
      return this.$route?.path?.toLowerCase()?.includes?.("export");
    }
  },
  watch: {
    airLoading: {
      handler() {
        this.firstLoad = true;
      }
    }
  },
  async mounted() {
    if (this.airData && this.airData.length === 0) {
      await this.getAirBookings(this.$route?.path?.toLowerCase()?.includes?.("export"));
    }
  },
  methods: {
    ...mapActions(airLcl, ["getAirBookings", "updateAirPaginationToken"]),
    ...mapActions(shipments, ["updateTimeFrame"]),
    dateFormat,
    showDetails(index) {
      if (this.selectedRow === index) {
        this.selectedRow = null;
      } else {
        this.selectedRow = index;
      }
    },
    async navigateToAirBooking() {
      this.$sendGAEvent({
        event: "DQCS",
        category: "Component",
        action: "Click",
        label: { data: "SoAirBookingClick" }
      });
      window.location.href = `/lcl/air`;
    },
    async updateComponentTimeFrame($event) {
      this.updateTimeFrame($event);
      this.timeFrame = $event.target.value;
      this.updateAirPaginationToken(1);
      await this.getAirBookings(this.$route?.path?.toLowerCase()?.includes?.("export"));
    },
    async getData(page) {
      this.updateAirPaginationToken(page);
      await this.getAirBookings(this.$route?.path?.toLowerCase()?.includes?.("export"));
    },
    async referenceSearch() {
      await this.getAirBookings(this.$route?.path?.toLowerCase()?.includes?.("export"));
      this.$sendGAEvent({
        event: "DQCS",
        category: "Component",
        action: "Click",
        label: { data: "soAirRefSearch" }
      });
    },
    marketingClickThrough() {
      this.$sendGAEvent({
        category: "SO_OutboundLink",
        event: "DQCS",
        action: "Click",
        label: {
          data: "SoAirLinkClick",
          isExport: !!this.$route?.path?.toLowerCase()?.includes?.("export")
        }
      });
    },
    PTClickThrough() {
      this.$sendGAEvent({
        category: "SO_OutboundLink",
        event: "DQCS",
        action: "Click",
        label: {
          data: "SOAirPTLinkClickedExport",
          isExport: !!this.$route?.path?.toLowerCase()?.includes?.("export")
        }
      });
    }
  }
};
</script>
